/**
 * Date Range Selector Utilities
 *
 * Date calculation functions and API conversion utilities.
 */

import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  subDays,
  subWeeks,
  subMonths,
  format,
} from "date-fns";
import type { DateRangeValue, DatePreset } from "./types";
import { isCustomRange } from "./types";

/** Get the start and end dates for a preset */
export function getDateRangeFromPreset(preset: DatePreset): { from: Date; to: Date } {
  const now = new Date();
  const today = startOfDay(now);
  const todayEnd = endOfDay(now);

  switch (preset) {
    case "today":
      return { from: today, to: todayEnd };

    case "yesterday": {
      const yesterday = subDays(today, 1);
      return { from: yesterday, to: endOfDay(yesterday) };
    }

    case "this_week":
      return {
        from: startOfWeek(today, { weekStartsOn: 1 }), // Monday
        to: todayEnd,
      };

    case "last_week": {
      const lastWeekStart = startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
      const lastWeekEnd = endOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
      return { from: lastWeekStart, to: lastWeekEnd };
    }

    case "this_month":
      return { from: startOfMonth(today), to: todayEnd };

    case "last_month": {
      const lastMonth = subMonths(today, 1);
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
    }

    case "last_7_days":
      return { from: subDays(today, 6), to: todayEnd };

    case "last_30_days":
      return { from: subDays(today, 29), to: todayEnd };

    case "last_90_days":
      return { from: subDays(today, 89), to: todayEnd };

    case "ytd":
      return { from: startOfYear(today), to: todayEnd };

    case "all":
      // Return a very old date for "all time"
      return { from: new Date(2020, 0, 1), to: todayEnd };

    default:
      return { from: today, to: todayEnd };
  }
}

/** Get the date range from any DateRangeValue */
export function getDateRange(value: DateRangeValue): { from: Date; to: Date } {
  if (isCustomRange(value)) {
    return { from: value.from, to: value.to };
  }
  return getDateRangeFromPreset(value);
}

/**
 * Convert DateRangeValue to backend API period format.
 * Backend expects: "24h", "7d", "30d", "90d", "180d", "365d", "all"
 */
export function toApiPeriod(value: DateRangeValue): string {
  if (isCustomRange(value)) {
    // For custom ranges, calculate the number of days
    const days = Math.ceil(
      (value.to.getTime() - value.from.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (days <= 1) return "24h";
    if (days <= 7) return "7d";
    if (days <= 30) return "30d";
    if (days <= 90) return "90d";
    if (days <= 180) return "180d";
    if (days <= 365) return "365d";
    return "all";
  }

  // Map presets to API format
  switch (value) {
    case "today":
    case "yesterday":
      return "24h";
    case "this_week":
    case "last_week":
    case "last_7_days":
      return "7d";
    case "this_month":
    case "last_month":
    case "last_30_days":
      return "30d";
    case "last_90_days":
      return "90d";
    case "ytd":
      return "365d";
    case "all":
      return "all";
    default:
      return "30d";
  }
}

/**
 * Get a display label for the current value.
 * For presets, returns the preset label.
 * For custom ranges, returns formatted date range.
 */
export function getDisplayLabel(value: DateRangeValue): string {
  if (isCustomRange(value)) {
    const fromStr = format(value.from, "MMM d");
    const toStr = format(value.to, "MMM d");
    // If same year, show shorter format
    if (value.from.getFullYear() === value.to.getFullYear()) {
      if (fromStr === toStr) {
        return fromStr;
      }
      return `${fromStr} - ${toStr}`;
    }
    // Different years, show full dates
    return `${format(value.from, "MMM d, yyyy")} - ${format(value.to, "MMM d, yyyy")}`;
  }
  return ""; // Presets use their label from config
}

/** Format a date for display in the calendar picker */
export function formatDateForInput(date: Date | undefined): string {
  if (!date) return "";
  return format(date, "MMM d, yyyy");
}

